<template>
  <div class="flows-layout">
    <Sidebar />

    <div class="main-content">
      <TopHeader />

      <main class="page-content">
        <div class="flows-container">
          <!-- Page Header -->
          <div class="page-header">
            <div class="header-content">
              <h1>WhatsApp Flows</h1>
              <p class="subtitle">Create interactive forms and experiences within WhatsApp conversations</p>
            </div>
            <button @click="openCreateModal" class="create-flow-btn">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
              </svg>
              Create Flow
            </button>
          </div>

          <!-- Stats Overview -->
          <div class="stats-grid">
            <div class="stat-card">
              <div class="stat-icon" style="background: rgba(37, 211, 102, 0.1);">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#25D366">
                  <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                </svg>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ stats.total_flows }}</div>
                <div class="stat-label">Total Flows</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon" style="background: rgba(59, 130, 246, 0.1);">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#3B82F6">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ stats.published_flows }}</div>
                <div class="stat-label">Published</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon" style="background: rgba(168, 85, 247, 0.1);">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#A855F7">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                </svg>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ stats.draft_flows }}</div>
                <div class="stat-label">Drafts</div>
              </div>
            </div>

            <div class="stat-card">
              <div class="stat-icon" style="background: rgba(251, 146, 60, 0.1);">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="#FB923C">
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                </svg>
              </div>
              <div class="stat-content">
                <div class="stat-value">{{ stats.deprecated_flows }}</div>
                <div class="stat-label">Deprecated</div>
              </div>
            </div>
          </div>

          <!-- Flows Tabs -->
          <div class="flows-section">
            <div class="tabs-header">
              <button
                v-for="tab in tabs"
                :key="tab.value"
                @click="activeTab = tab.value"
                :class="['tab-btn', { active: activeTab === tab.value }]"
              >
                {{ tab.label }}
                <span v-if="tab.count > 0" class="tab-count">{{ tab.count }}</span>
              </button>
            </div>

            <!-- Loading State -->
            <div v-if="loading" class="loading-state">
              <div class="loading-spinner"></div>
              <p>Loading flows...</p>
            </div>

            <!-- Empty State -->
            <div v-else-if="filteredFlows.length === 0" class="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                <polyline points="14 2 14 8 20 8"/>
                <line x1="16" y1="13" x2="8" y2="13"/>
                <line x1="16" y1="17" x2="8" y2="17"/>
                <polyline points="10 9 9 9 8 9"/>
              </svg>
              <h3>No flows found</h3>
              <p>Create your first WhatsApp Flow to start collecting structured data from customers</p>
              <button @click="openCreateModal" class="empty-action-btn">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/>
                </svg>
                Create Flow
              </button>
            </div>

            <!-- Flows List -->
            <div v-else class="flows-list">
              <div v-for="flow in filteredFlows" :key="flow.id" class="flow-card">
                <div class="flow-header">
                  <div class="flow-info">
                    <h3 class="flow-name">{{ flow.name }}</h3>
                    <div class="flow-meta">
                      <span class="flow-id">ID: {{ flow.id }}</span>
                      <span class="flow-category" v-if="flow.categories && flow.categories.length > 0">
                        {{ flow.categories.join(', ') }}
                      </span>
                    </div>
                  </div>
                  <div class="flow-status-badge" :class="flow.status.toLowerCase()">
                    {{ flow.status }}
                  </div>
                </div>

                <div class="flow-actions">
                  <button @click="viewFlow(flow)" class="action-btn view-btn" title="View Details">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                    </svg>
                    View
                  </button>

                  <button
                    v-if="flow.status === 'DRAFT'"
                    @click="editFlowBuilder(flow)"
                    class="action-btn edit-btn"
                    title="Edit in Flow Builder"
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z"/>
                    </svg>
                    Edit Flow
                  </button>

                  <button
                    v-if="flow.status === 'DRAFT'"
                    @click="publishFlow(flow)"
                    class="action-btn publish-btn"
                    :disabled="publishingFlowId === flow.id"
                    title="Publish Flow"
                  >
                    <svg v-if="publishingFlowId !== flow.id" width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M5 4v2h14V4H5zm0 10h4v6h6v-6h4l-7-7-7 7z"/>
                    </svg>
                    <span class="spinner-small" v-else></span>
                    {{ publishingFlowId === flow.id ? 'Publishing...' : 'Publish' }}
                  </button>

                  <button @click="deleteFlow(flow)" class="action-btn delete-btn" title="Delete Flow">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/>
                    </svg>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>

    <!-- Create Flow Modal -->
    <div v-if="showCreateModal" class="modal-overlay" @click.self="closeCreateModal">
      <div class="modal-container">
        <div class="modal-header">
          <h2>Create New Flow</h2>
          <button @click="closeCreateModal" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="form-group">
            <label for="flowName">Flow Name</label>
            <input
              id="flowName"
              v-model="newFlow.name"
              type="text"
              placeholder="e.g., Customer Feedback Survey"
              maxlength="100"
              class="form-input"
            />
          </div>

          <div class="form-group">
            <label for="flowCategory">Category</label>
            <select id="flowCategory" v-model="newFlow.category" class="form-select">
              <option value="OTHER">Other</option>
              <option value="SIGN_UP">Sign Up</option>
              <option value="SIGN_IN">Sign In</option>
              <option value="APPOINTMENT_BOOKING">Appointment Booking</option>
              <option value="LEAD_GENERATION">Lead Generation</option>
              <option value="CONTACT_US">Contact Us</option>
              <option value="CUSTOMER_SUPPORT">Customer Support</option>
              <option value="SURVEY">Survey</option>
              <option value="POLL">Poll</option>
            </select>
          </div>

          <div class="modal-info">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="#3B82F6">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
            <p>After creating the flow, use our Flow Builder to design interactive forms with drag-and-drop components.</p>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeCreateModal" class="cancel-btn">Cancel</button>
          <button
            @click="createFlow"
            :disabled="!newFlow.name || isCreating"
            class="create-btn"
          >
            <span v-if="!isCreating">Create Flow</span>
            <span v-else class="loading-spinner-inline">
              <span class="spinner-small"></span>
              Creating...
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- View Flow Modal -->
    <div v-if="selectedFlow" class="modal-overlay" @click.self="closeViewModal">
      <div class="modal-container large">
        <div class="modal-header">
          <h2>Flow Details</h2>
          <button @click="closeViewModal" class="close-btn">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
            </svg>
          </button>
        </div>

        <div class="modal-body">
          <div class="flow-details">
            <div class="detail-row">
              <span class="detail-label">Name:</span>
              <span class="detail-value">{{ selectedFlow.name }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">ID:</span>
              <span class="detail-value">{{ selectedFlow.id }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Status:</span>
              <span class="detail-value">
                <span class="flow-status-badge" :class="selectedFlow.status.toLowerCase()">
                  {{ selectedFlow.status }}
                </span>
              </span>
            </div>
            <div class="detail-row" v-if="selectedFlow.categories">
              <span class="detail-label">Categories:</span>
              <span class="detail-value">{{ selectedFlow.categories.join(', ') }}</span>
            </div>
            <div class="detail-row" v-if="selectedFlow.validation_errors && selectedFlow.validation_errors.length > 0">
              <span class="detail-label">Validation Errors:</span>
              <div class="error-list">
                <div v-for="(error, index) in selectedFlow.validation_errors" :key="index" class="error-item">
                  {{ error.error }}
                </div>
              </div>
            </div>
          </div>

          <div class="flow-actions-section">
            <a
              :href="`https://business.facebook.com/wa/manage/flows/${selectedFlow.id}/`"
              target="_blank"
              class="action-link-btn"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z"/>
              </svg>
              Edit in Flow Builder
            </a>
          </div>
        </div>

        <div class="modal-footer">
          <button @click="closeViewModal" class="cancel-btn">Close</button>
        </div>
      </div>
    </div>

    <!-- Success/Error Messages -->
    <div v-if="successMessage" class="toast toast-success">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
      </svg>
      {{ successMessage }}
    </div>

    <div v-if="errorMessage" class="toast toast-error">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 2C6.47 2 2 6.47 2 12s4.47 10 10 10 10-4.47 10-10S17.53 2 12 2zm5 13.59L15.59 17 12 13.41 8.41 17 7 15.59 10.59 12 7 8.41 8.41 7 12 10.59 15.59 7 17 8.41 13.41 12 17 15.59z"/>
      </svg>
      {{ errorMessage }}
    </div>

    <!-- Flow Builder Modal -->
    <FlowBuilderModal
      :is-open="showFlowBuilder"
      :flow-id="editingFlowId"
      :flow-data="editingFlowData"
      @close="closeFlowBuilder"
      @flow-saved="handleFlowSaved"
    />
  </div>
</template>

<script>
import Sidebar from '@/components/Sidebar.vue'
import TopHeader from '@/components/TopHeader.vue'
import FlowBuilderModal from '@/components/FlowBuilderModal.vue'
import { AuthService } from '@/services/auth'

export default {
  name: 'WhatsAppFlowsView',
  components: {
    Sidebar,
    TopHeader,
    FlowBuilderModal
  },
  data () {
    return {
      flows: [],
      activeTab: 'all',
      loading: true,
      showCreateModal: false,
      selectedFlow: null,
      isCreating: false,
      publishingFlowId: null,
      successMessage: '',
      errorMessage: '',
      showFlowBuilder: false,
      editingFlowId: null,
      editingFlowData: null,
      newFlow: {
        name: '',
        category: 'OTHER'
      },
      tabs: [
        { label: 'All', value: 'all', count: 0 },
        { label: 'Published', value: 'PUBLISHED', count: 0 },
        { label: 'Draft', value: 'DRAFT', count: 0 },
        { label: 'Deprecated', value: 'DEPRECATED', count: 0 }
      ]
    }
  },
  computed: {
    filteredFlows () {
      if (this.activeTab === 'all') {
        return this.flows
      }
      return this.flows.filter(flow => flow.status === this.activeTab)
    },
    stats () {
      return {
        total_flows: this.flows.length,
        published_flows: this.flows.filter(f => f.status === 'PUBLISHED').length,
        draft_flows: this.flows.filter(f => f.status === 'DRAFT').length,
        deprecated_flows: this.flows.filter(f => f.status === 'DEPRECATED').length
      }
    }
  },
  mounted () {
    this.loadFlows()
  },
  methods: {
    async loadFlows () {
      try {
        this.loading = true
        const token = AuthService.getAuthToken()

        const response = await fetch('https://api.quali-d.com/whatsapp/flows', {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          this.flows = data.flows || []
          this.updateTabCounts()
        } else {
          this.showError(data.message || 'Failed to load flows')
        }
      } catch (error) {
        console.error('Error loading flows:', error)
        this.showError('Failed to load flows')
      } finally {
        this.loading = false
      }
    },

    updateTabCounts () {
      this.tabs[0].count = this.flows.length
      this.tabs[1].count = this.flows.filter(f => f.status === 'PUBLISHED').length
      this.tabs[2].count = this.flows.filter(f => f.status === 'DRAFT').length
      this.tabs[3].count = this.flows.filter(f => f.status === 'DEPRECATED').length
    },

    openCreateModal () {
      this.newFlow = {
        name: '',
        category: 'OTHER'
      }
      this.showCreateModal = true
    },

    closeCreateModal () {
      this.showCreateModal = false
    },

    async createFlow () {
      try {
        this.isCreating = true
        const token = AuthService.getAuthToken()

        const response = await fetch('https://api.quali-d.com/whatsapp/flows', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            name: this.newFlow.name,
            categories: [this.newFlow.category]
          })
        })

        const data = await response.json()

        if (data.success) {
          this.showSuccess('Flow created successfully! Opening Flow Builder...')
          this.closeCreateModal()

          // Auto-open flow builder for brand new flow WITHOUT loading from WhatsApp
          // This ensures we start with a clean template instead of WhatsApp's auto-generated template
          const newFlowId = data.flow.id
          this.editingFlowId = newFlowId
          this.editingFlowData = null // Don't pass flowData - this will trigger resetFlow() in FlowBuilderModal
          this.showFlowBuilder = true

          // Reload flows list in background
          await this.loadFlows()
        } else {
          // Show detailed error from API
          let errorMsg = data.message || 'Failed to create flow'
          if (data.details && data.details.error) {
            const apiError = data.details.error
            if (apiError.error_user_msg) {
              errorMsg = apiError.error_user_msg
            } else if (apiError.message) {
              errorMsg = apiError.message
            }
            // Add specific error details
            if (apiError.error_user_title) {
              errorMsg = `${apiError.error_user_title}: ${errorMsg}`
            }
          }
          this.showError(errorMsg)
        }
      } catch (error) {
        console.error('Error creating flow:', error)
        this.showError('Failed to create flow. Please check your connection and try again.')
      } finally {
        this.isCreating = false
      }
    },

    async viewFlow (flow) {
      try {
        const token = AuthService.getAuthToken()

        const response = await fetch(`https://api.quali-d.com/whatsapp/flows/${flow.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          this.selectedFlow = data.flow
        } else {
          this.showError(data.message || 'Failed to load flow details')
        }
      } catch (error) {
        console.error('Error loading flow:', error)
        this.showError('Failed to load flow details')
      }
    },

    closeViewModal () {
      this.selectedFlow = null
    },

    async publishFlow (flow) {
      if (!confirm(`Are you sure you want to publish "${flow.name}"? Once published, it can be sent to customers.`)) {
        return
      }

      try {
        this.publishingFlowId = flow.id
        const token = AuthService.getAuthToken()

        const response = await fetch(`https://api.quali-d.com/whatsapp/flows/${flow.id}/publish`, {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          this.showSuccess('Flow published successfully!')
          await this.loadFlows()
        } else {
          this.showError(data.message || 'Failed to publish flow')
        }
      } catch (error) {
        console.error('Error publishing flow:', error)
        this.showError('Failed to publish flow')
      } finally {
        this.publishingFlowId = null
      }
    },

    async deleteFlow (flow) {
      if (!confirm(`Are you sure you want to delete "${flow.name}"? This action cannot be undone.`)) {
        return
      }

      try {
        const token = AuthService.getAuthToken()

        const response = await fetch(`https://api.quali-d.com/whatsapp/flows/${flow.id}`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          this.showSuccess('Flow deleted successfully!')
          await this.loadFlows()
        } else {
          this.showError(data.message || 'Failed to delete flow')
        }
      } catch (error) {
        console.error('Error deleting flow:', error)
        this.showError('Failed to delete flow')
      }
    },

    showSuccess (message) {
      this.successMessage = message
      setTimeout(() => {
        this.successMessage = ''
      }, 3000)
    },

    async editFlowBuilder (flow) {
      try {
        const token = AuthService.getAuthToken()

        // Get full flow details including JSON
        const response = await fetch(`https://api.quali-d.com/whatsapp/flows/${flow.id}`, {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json'
          }
        })

        const data = await response.json()

        if (data.success) {
          this.editingFlowId = flow.id
          this.editingFlowData = data.flow
          this.showFlowBuilder = true
        } else {
          this.showError(data.message || 'Failed to load flow for editing')
        }
      } catch (error) {
        console.error('Error loading flow for editing:', error)
        this.showError('Failed to load flow for editing')
      }
    },

    closeFlowBuilder () {
      this.showFlowBuilder = false
      this.editingFlowId = null
      this.editingFlowData = null
    },

    async handleFlowSaved (flowData) {
      this.showSuccess('Flow saved successfully!')
      await this.loadFlows()
      this.closeFlowBuilder()
    },

    showError (message) {
      this.errorMessage = message
      setTimeout(() => {
        this.errorMessage = ''
      }, 5000)
    }
  }
}
</script>

<style scoped>
.flows-layout {
  display: flex;
  min-height: 100vh;
  background: var(--bg-secondary);
}

.main-content {
  flex: 1;
  margin-left: 280px;
  display: flex;
  flex-direction: column;
}

.page-content {
  flex: 1;
  padding: 24px;
  overflow-y: auto;
}

.flows-container {
  max-width: 1400px;
  margin: 0 auto;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 32px;
  gap: 24px;
}

.header-content h1 {
  font-size: 32px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.subtitle {
  font-size: 16px;
  color: var(--text-secondary);
  margin: 0;
}

.create-flow-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, #25D366 0%, #128C7E 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  box-shadow: 0 4px 12px rgba(37, 211, 102, 0.3);
  white-space: nowrap;
}

.create-flow-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(37, 211, 102, 0.4);
}

/* Stats Grid */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 20px;
  margin-bottom: 32px;
}

.stat-card {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 16px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  transition: all 0.2s;
}

.stat-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.stat-icon {
  width: 56px;
  height: 56px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.stat-content {
  flex: 1;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: var(--text-secondary);
  font-weight: 500;
}

/* Tabs */
.flows-section {
  background: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 16px;
  overflow: hidden;
}

.tabs-header {
  display: flex;
  border-bottom: 1px solid var(--border-primary);
  background: var(--bg-secondary);
  padding: 0 24px;
}

.tab-btn {
  padding: 16px 24px;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  color: var(--text-secondary);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 8px;
}

.tab-btn:hover {
  color: var(--text-primary);
  background: rgba(59, 130, 246, 0.05);
}

.tab-btn.active {
  color: #3B82F6;
  border-bottom-color: #3B82F6;
  background: rgba(59, 130, 246, 0.05);
}

.tab-count {
  background: var(--bg-primary);
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  min-width: 24px;
  text-align: center;
}

.tab-btn.active .tab-count {
  background: #3B82F6;
  color: white;
}

/* Loading State */
.loading-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  color: var(--text-secondary);
}

.loading-spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--border-primary);
  border-top-color: #3B82F6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Empty State */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  text-align: center;
}

.empty-state svg {
  color: var(--text-tertiary);
  margin-bottom: 16px;
}

.empty-state h3 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.empty-state p {
  font-size: 15px;
  color: var(--text-secondary);
  margin: 0 0 24px;
  max-width: 400px;
}

.empty-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3B82F6;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.empty-action-btn:hover {
  background: #2563EB;
  transform: translateY(-1px);
}

/* Flows List */
.flows-list {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.flow-card {
  background: var(--bg-secondary);
  border: 1px solid var(--border-primary);
  border-radius: 12px;
  padding: 20px;
  transition: all 0.2s;
}

.flow-card:hover {
  border-color: var(--border-secondary);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.flow-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 16px;
  gap: 16px;
}

.flow-info {
  flex: 1;
  min-width: 0;
}

.flow-name {
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0 0 8px;
}

.flow-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 13px;
  color: var(--text-secondary);
}

.flow-id {
  font-family: 'Courier New', monospace;
}

.flow-category {
  padding: 2px 8px;
  background: rgba(59, 130, 246, 0.1);
  color: #3B82F6;
  border-radius: 6px;
  font-weight: 500;
}

.flow-status-badge {
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
}

.flow-status-badge.published {
  background: #D1FAE5;
  color: #065F46;
}

.flow-status-badge.draft {
  background: #FEF3C7;
  color: #92400E;
}

.flow-status-badge.deprecated {
  background: #FEE2E2;
  color: #991B1B;
}

.flow-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border: 1px solid var(--border-primary);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--bg-primary);
  color: var(--text-secondary);
}

.action-btn:hover:not(:disabled) {
  border-color: var(--border-secondary);
  background: var(--bg-secondary);
  color: var(--text-primary);
}

.action-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.edit-btn {
  background: #F0FDF4;
  color: #16A34A;
  border-color: #86EFAC;
}

.edit-btn:hover:not(:disabled) {
  background: #DCFCE7;
  border-color: #4ADE80;
}

.publish-btn {
  background: #DBEAFE;
  color: #1E40AF;
  border-color: #93C5FD;
}

.publish-btn:hover:not(:disabled) {
  background: #BFDBFE;
  border-color: #60A5FA;
}

.delete-btn {
  color: #DC2626;
  border-color: #FCA5A5;
}

.delete-btn:hover:not(:disabled) {
  background: #FEE2E2;
  border-color: #EF4444;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 24px;
}

.modal-container {
  background: var(--bg-primary);
  border-radius: 16px;
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-container.large {
  max-width: 800px;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 24px;
  border-bottom: 1px solid var(--border-primary);
}

.modal-header h2 {
  font-size: 20px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.close-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

.modal-body {
  padding: 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 8px;
}

.form-input,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 2px solid var(--border-primary);
  border-radius: 10px;
  font-size: 15px;
  background: var(--bg-primary);
  color: var(--text-primary);
  transition: all 0.2s;
}

.form-input:focus,
.form-select:focus {
  outline: none;
  border-color: #3B82F6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.modal-info {
  display: flex;
  gap: 12px;
  padding: 16px;
  background: rgba(59, 130, 246, 0.05);
  border: 1px solid rgba(59, 130, 246, 0.2);
  border-radius: 10px;
  margin-top: 16px;
}

.modal-info p {
  margin: 0;
  font-size: 14px;
  color: var(--text-secondary);
  line-height: 1.5;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 24px;
  border-top: 1px solid var(--border-primary);
}

.cancel-btn {
  padding: 10px 20px;
  border: 1px solid var(--border-primary);
  border-radius: 10px;
  background: var(--bg-secondary);
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.cancel-btn:hover {
  border-color: var(--border-secondary);
  color: var(--text-primary);
}

.create-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 10px;
  background: #3B82F6;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 120px;
}

.create-btn:hover:not(:disabled) {
  background: #2563EB;
  transform: translateY(-1px);
}

.create-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.loading-spinner-inline {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.spinner-small {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: white;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  display: inline-block;
}

/* Flow Details */
.flow-details {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.detail-row {
  display: flex;
  gap: 12px;
}

.detail-label {
  font-weight: 600;
  color: var(--text-secondary);
  min-width: 120px;
}

.detail-value {
  color: var(--text-primary);
  flex: 1;
}

.error-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.error-item {
  padding: 8px 12px;
  background: #FEE2E2;
  color: #991B1B;
  border-radius: 6px;
  font-size: 13px;
}

.flow-actions-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--border-primary);
}

.action-link-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: #3B82F6;
  color: white;
  border-radius: 10px;
  text-decoration: none;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s;
}

.action-link-btn:hover {
  background: #2563EB;
  transform: translateY(-1px);
}

/* Toast Messages */
.toast {
  position: fixed;
  bottom: 24px;
  right: 24px;
  padding: 16px 20px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-weight: 500;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  animation: slideIn 0.3s ease-out;
}

@keyframes slideIn {
  from {
    transform: translateX(400px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

.toast-success {
  background: #D1FAE5;
  color: #065F46;
}

.toast-error {
  background: #FEE2E2;
  color: #991B1B;
}

/* Responsive */
@media (max-width: 768px) {
  .main-content {
    margin-left: 0;
    margin-top: 64px;
  }

  .page-header {
    flex-direction: column;
  }

  .create-flow-btn {
    width: 100%;
    justify-content: center;
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .tabs-header {
    overflow-x: auto;
    padding: 0 16px;
  }

  .flows-list {
    padding: 16px;
  }

  .flow-actions {
    width: 100%;
  }

  .action-btn {
    flex: 1;
    justify-content: center;
  }
}
</style>
